import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectCountryList, selectFocusedRowId, selectGbpLocationGroups, selectHoveredRowId, selectLocations, selectSelectedRowsIds } from './state/location-list-selectors';
import { CellContextMenuEvent, CellDoubleClickedEvent, CellEditRequestEvent, CellFocusedEvent, CellMouseOverEvent, ColDef, Column, ColumnMovedEvent, ColumnPinnedEvent, ColumnResizedEvent, ColumnState, ColumnVisibleEvent, FilterChangedEvent, GetRowIdParams, GridOptions, GridReadyEvent, ICellRendererParams, ITextFilterParams, PaginationChangedEvent, RowDataUpdatedEvent, SelectionChangedEvent, SortChangedEvent, SuppressKeyboardEventParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { LSCField } from '../../../models/LSCFields';
import { getChangeableGmbFields, getFilterableGmbFields, getFullNameForFieldID } from '../location-utils';
import { AgTextFloatingFilterComponent } from '../../global/gx-ag-grid/ag-text-floating-filter/ag-text-floating-filter.component';
import * as skeletonSelectors from '../state/locations-skeleton-selectors';
import { EditLocationActions, LocationsLifecycleActions, RowActions } from './state/location-list-actions';
import { BusinessHoursCellComponent } from './cells/business-hours-cell/business-hours-cell.component';
import { AgListFloatingFilterComponent, ListFloatingFilterParams } from '../../global/gx-ag-grid/ag-list-floating-filter/ag-list-floating-filter.component';
import { AgMultiSelectFilterComponent } from '../../global/gx-ag-grid/ag-multi-select-filter/ag-multi-select-filter.component';
import { filter, map, skip, take, takeUntil } from 'rxjs/operators';
import { selectAccountID } from '../../../store/global/core-selectors';
import { formatLocalizedDate } from '../../../core/utils/date-fns-utils';
import { MultiLineTextCellComponentParams } from './cells/multi-line-text-cell/multi-line-text-cell-local-store.service';
import { CcCompletionCellComponent } from './cells/cc-completion-cell/cc-completion-cell.component';
import { LocationListLocalStoreService } from './location-list-local-store.service';
import { getBlacklistedFieldsAsString, getCustomServiceAsString, getDifferenceFieldsAsString, prettifyJSON } from './location-list-utils';
import { LinkCellComponent } from './cells/link-cell/link-cell.component';
import { MultiLineTextCellComponent } from './cells/multi-line-text-cell/multi-line-text-cell.component';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { SimpleTextCellComponent } from './cells/simple-text-cell/simple-text-cell.component';
import { cloneDeep, isEqual } from 'lodash';
import { CoordinateCellComponent } from './cells/coordinate-cell/coordinate-cell.component';
import { PhotoCellComponent } from './cells/photo-cell/photo-cell.component';
import { StatusCellComponent } from './cells/status-cell/status-cell.component';
import { RowMenuCellComponent } from './cells/row-menu-cell/row-menu-cell.component';
import { sendToGoogleAnalytics } from '../../../core/utilities';
import { EventManager } from '@angular/platform-browser';
import { MatMenuTrigger } from '@angular/material/menu';
import { AgChipFilterComponent } from '../../global/gx-ag-grid/ag-chip-filter/ag-chip-filter.component';
import { StatusFilterComponent } from './filters/status-filter/status-filter.component';
import { DataColumnHeaderComponent, DataColumnHeaderParams } from './headers/data-column-header/data-column-header.component';
import { isFieldWithDataSource } from '../data-source-utils';
import { RowMenuHeaderComponent } from './headers/row-menu-header/row-menu-header.component';
import { LocationListApiService } from './location-list-api.service';
import { MatPaginator } from '@angular/material/paginator';
import { SkeletonBottomPanelActions, SkeletonRightPanelActions } from '../state/locations-skeleton-actions';
import { TranslateService } from '@ngx-translate/core';
import { SimpleTextEditorComponent } from './editors/simple-text-editor/simple-text-editor.component';
import { COLUMNS_VISIBLE_BY_DEFAULT, DEFAULT_GRID_COLUMN_DEFS, DEFAULT_GRID_OPTIONS, MAX_JAVA_INT } from '../constants';
import { RecentUpdateFloatingFilterComponent } from './filters/recent-update-floating-filter/recent-update-floating-filter.component';
import { FilterModel } from '../../../models/locations-models/filter-map';
import { HotkeysService } from '../../global/hotkeys/hotkeys.service';
import { Location } from '../../../models/locations-models/location';
import { GbpLocationGroup } from '../../../models/locations-models/gbp-location-group';
import { BehaviorSubject } from 'rxjs';
import { CoordinatesEditorComponent } from './editors/coordinates-editor/coordinates-editor.component';
import { SocialMediaLinksEditorComponent } from './editors/social-media-links-editor/social-media-links-editor.component';
import { BusinessHoursEditorComponent } from './editors/business-hours-editor/business-hours-editor.component';
import { StatusEditorComponent } from './editors/status-editor/status-editor.component';
import { CustomServicesEditorComponent } from './editors/custom-services-editor/custom-services-editor.component';
import { Clipboard } from '@angular/cdk/clipboard';
import { FeedbackService } from '../../../core/feedback.service';
import { RowMenuComponent } from './row-menu/row-menu.component';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { JumperComponent } from '../../global/jumper/jumper.component';
import { StatusFilterLegendComponent } from './status-filter-legend/status-filter-legend.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AgGridAngular } from 'ag-grid-angular';
import { LocationListActionBarComponent } from './location-list-action-bar/location-list-action-bar.component';
import { LetDirective } from '@ngrx/component';
import { AgDateTimeFilterComponent } from '../../global/gx-ag-grid/ag-date-time-filter/ag-date-time-filter.component';
import { AgDateTimeFloatingFilterComponent } from '../../global/gx-ag-grid/ag-date-time-floating-filter/ag-date-time-floating-filter.component';

@Component({
    selector: 'app-location-list',
    templateUrl: './location-list.component.html',
    styleUrls: ['./location-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
  imports: [
    LetDirective,
    LocationListActionBarComponent,
    AgGridAngular,
    MatProgressSpinner,
    StatusFilterLegendComponent,
    MatPaginator,
    JumperComponent,
    NgClass,
    MatIcon,
    RowMenuComponent,
    MatMenuTrigger,
    MatIconButton,
  ],
})
export class LocationListComponent implements AfterViewInit {
  readonly gridContainerId = 'grid-container';

  // Right-click menu
  @ViewChild('rowMenuTrigger') rowMenuTrigger: MatMenuTrigger;
  @ViewChild('paginatorElement', { read: ElementRef }) paginatorElement: ElementRef;
  @ViewChild('paginator') paginator: MatPaginator;

  // Since the Grid's value getters are pure functions, they won't work w/ observables.
  // The GBP groups column's value getter needs to fetch data from the store to map group names.
  // This needs to be done in the value getter, otherwise built-in sorting will not work properly.
  gbpLocationGroups: BehaviorSubject<GbpLocationGroup[]> = new BehaviorSubject([]);

  locationListViewModel$ = this.localStore.select({
    // make sure to show the grid AFTER GBP groups are loaded, since the GBP group filter will
    // reset its cached value if it cannot find its filter options which are generated from GBP groups
    loadingGbpLocationGroups: this.gbpLocationGroups.asObservable().pipe(map((gbpGroups) => !gbpGroups?.length)),
    total: this.localStore.total$,
    pageIndex: this.localStore.pageIndex$,
    pageSize: this.localStore.pageSize$,
    itemsPerPage: this.localStore.itemsPerPage$,
    skeletonLayout: this.store.select(skeletonSelectors.state),
    locations: this.store.select(selectLocations),
    columnState: this.localStore.columnStateArray$,
    focusedRowId: this.store.select(selectFocusedRowId),
    hoveredRowId: this.store.select(selectHoveredRowId),
    selectedRowsIds: this.store.select(selectSelectedRowsIds),
    filterModel: this.localStore.filterModel$,
  });

  imagePreviewURL$ = this.localStore.imagePreviewURL$;
  rowMenuPosition$ = this.localStore.rowMenuPosition$;

  columnDefs: ColDef<Location>[] = this.getColumnDefs();

  defaultColumnDefs: ColDef<Location> = {
    ...DEFAULT_GRID_COLUMN_DEFS,
    // https://ag-grid.com/angular-data-grid/filter-api/#get--set-all-filter-models
    // allows filterModel to be applied synchronously (also if no data) - alternatively define
    // cellDataType for every column
    cellDataType: false,
    showDisabledCheckboxes: true,
    resizable: true,
    minWidth: 70,
    autoHeight: true,
    wrapText: true,
    // since all printable keys trigger edit mode
    // https://www.ag-grid.com/javascript-data-grid/cell-editing-start-stop/
    // filter out 'shift' + 'c' key press since that shortcut is reserved for copy cell to
    // clipboard functionality
    // https://www.ag-grid.com/javascript-data-grid/keyboard-navigation/#suppress-keyboard-events
    suppressKeyboardEvent: (params: SuppressKeyboardEventParams<Location>) => {
      return (params.event.key === 'C' || params.event.key === 'c') && params.event.shiftKey
    }
  };

  gridOptions: GridOptions<Location> = {
    ...DEFAULT_GRID_OPTIONS,
    getRowId: this.getRowId.bind(this),
    suppressDragLeaveHidesColumns: true,
    suppressRowHoverHighlight: true, // handled by local store effect
    rowSelection: {
      mode: 'multiRow',
      // selects rows that pass active filters and are on the current page
      selectAll: 'currentPage'
    },
    selectionColumnDef: {
      sortable: false,
      minWidth: 66,
      maxWidth: 66,
      suppressHeaderMenuButton: true,
      pinned: 'left',
      lockPinned: true,
      headerClass: 'selectHeader',
      // vertical alignment same as any other cell
      cellStyle: { paddingTop: '15px', paddingBottom: '15px' },
      onCellClicked: e => {
        if (e?.event?.['detail'] === 2) {
          return; // filter out dbl-clicks
        }
        // toggle row selection (so that the user does not have to click the checkbox directly)
        this.locationListApi.gridApi?.setNodesSelected({ nodes: [e.node], newValue: !e.node.isSelected() });
      }
    },
    suppressClickEdit: true,
    readOnlyEdit: true,
    preventDefaultOnContextMenu: true,
    pagination: true,
    suppressPaginationPanel: true,
  };

  componentDestroyed$ = this.actions.pipe(ofType(LocationsLifecycleActions.locationsComponentDestroyed));

  constructor(private store: Store,
              private actions: Actions,
              private localStore: LocationListLocalStoreService,
              private eventManager: EventManager,
              private locationListApi: LocationListApiService,
              private hotkeysService: HotkeysService,
              private clipboard: Clipboard,
              private feedbackService: FeedbackService,
              public translate: TranslateService) {

    this.store.select(selectAccountID)
        .pipe(
            // w8 till we have a value as the respective effects require accountID
            filter((accountID) => !!accountID),
            take(1),
            takeUntil(this.componentDestroyed$))
        .subscribe(() => this.store.dispatch(LocationsLifecycleActions.locationsComponentInitiated({})));

    this.store.select(selectGbpLocationGroups)
        .pipe(
            skip(1), // skip the default ([]) first value
            takeUntil(this.componentDestroyed$))
        .subscribe((gbpLocationGroups) => {
          this.gbpLocationGroups.next(gbpLocationGroups);
          // should gbp groups change (unlikely), refresh cell values
          this.locationListApi.gridApi?.refreshCells({ columns: [LSCField.gmbAccountIds] });
        });

    this.registerHotkeys();
  }

  ngAfterViewInit() {
    setTimeout(() => this.rewriteMaxPageSizeValue());
  }


  /**
   * Needed since we're using immutable data (ngrx)
   * https://www.ag-grid.com/angular-data-grid/data-update-row-data/
   * https://stackoverflow.com/questions/58879648/prevent-ag-grid-from-re-rendering-when-data-ngrx-store-changes
   * @param params
   */
  getRowId(params: GetRowIdParams<Location>) {
    return params.data.id.toString();
  }

  /**
   * Apply cached column state
   * @param e
   * @param columnState
   * @param selectedRowsIds
   * @param filterModel
   */
  onGridReady(e: GridReadyEvent<Location>,
              columnState: ColumnState[],
              selectedRowsIds: number[],
              filterModel: FilterModel) {
    this.locationListApi.gridApi = e.api;

    // apply column state
    if (columnState?.length) {
      // filter potentially undefined columns from cached state
      const filteredColumnState = columnState.filter((cs) =>
          e.api.getColumns().map(col => col.getColId()).includes(cs.colId));

      // nothing has been filtered out, so just rehydrate grid's column state
      if (filteredColumnState.length === columnState.length) {
        e.api.applyColumnState({ state: columnState, applyOrder: true });
      } else { // also sync local store
        e.api.applyColumnState({ state: filteredColumnState, applyOrder: true });
        this.localStore.updateColumnState(filteredColumnState);
      }

    } else { // reset local store and cache
      this.localStore.updateColumnState(e.api.getColumnState());
    }

    // apply filters
    this.locationListApi.setFilters(filterModel);

    // apply row selection in grid
    if (selectedRowsIds?.length) {
      const toBeSelectedNodes = [];
      e.api.forEachNode(rowNode => {
        if (selectedRowsIds.includes(parseInt(rowNode.id, 10))) {
          toBeSelectedNodes.push(rowNode);
        }
      });
      e.api.setNodesSelected({
        nodes: toBeSelectedNodes, newValue: true,
      });
    }

    // After the scheduled updater executes, changes to column state will be persisted in
    // browser cache.
    // 100ms is an arbitrary value but seems to work well to give enough time to filter out
    // events caused by executing applyColumnState() above
    setTimeout(() => this.localStore.updateGridReady(true), 100);
  }

  /**
   * Handle loading indication and issue edit API call
   * @param e
   */
  onCellEditRequest(e: CellEditRequestEvent<Location>) {
    let newValue = e.value; // value returned by the editor component
    let oldValue: any;

    switch (e.colDef.field) {
      case LSCField.coordinateImport:
        oldValue = e.data.coordinateImport;
        break;
      case LSCField.socialMediaUrls:
        oldValue = e.data.socialMediaUrls;
        break;
      case LSCField.businessHours as string:
        oldValue = {
          [LSCField.mondayHours]: e.data.mondayHours,
          [LSCField.tuesdayHours]: e.data.tuesdayHours,
          [LSCField.wednesdayHours]: e.data.wednesdayHours,
          [LSCField.thursdayHours]: e.data.thursdayHours,
          [LSCField.fridayHours]: e.data.fridayHours,
          [LSCField.saturdayHours]: e.data.saturdayHours,
          [LSCField.sundayHours]: e.data.sundayHours,
        };
        break;
      case LSCField.status:
        oldValue = {
          [LSCField.businessStatus]: e.data.businessStatus,
          [LSCField.managementStatus]: e.data.managementStatus,
        };
        break;
      case LSCField.customServices:
        oldValue = e.data.customServices;
        break;
      default:
        oldValue = e.oldValue; // e.oldValue is the old (formatted) value
        break;
    }

    if (!isEqual(newValue, oldValue)) { // only call API if save is needed
      this.localStore.loadingValues$.pipe(take(1), takeUntil(this.componentDestroyed$))
          .subscribe((loadingValues) => {
            // start loading indicator if it isn't running already
            if (!loadingValues[e.data.id]?.[e.colDef.field]) {
              this.localStore.startLoadingValue({
                locationId: e.data.id,
                field: (e.colDef.field) as LSCField,
              });
            }
          });

      const locationCopy: Location = cloneDeep(e.data);

      if (e.colDef.field === LSCField.businessHours as string) {
        locationCopy.mondayHours = newValue.mondayHours;
        locationCopy.tuesdayHours = newValue.tuesdayHours;
        locationCopy.wednesdayHours = newValue.wednesdayHours;
        locationCopy.thursdayHours = newValue.thursdayHours;
        locationCopy.fridayHours = newValue.fridayHours;
        locationCopy.saturdayHours = newValue.saturdayHours;
        locationCopy.sundayHours = newValue.sundayHours;
      } else if (e.colDef.field === LSCField.status) {
        locationCopy.businessStatus = newValue.businessStatus;
        locationCopy.managementStatus = newValue.managementStatus;
      } else {
        locationCopy[e.colDef.field] = newValue;
      }

      this.store.dispatch(EditLocationActions.editLocationData({
        location: locationCopy,
        singleField: (e.colDef.field) as LSCField,
      }));
    }
  }


  /**
   * Opens the 'rowMenu' after right-click on a row
   * @param e
   */
  onCellRightClick(e: CellContextMenuEvent) {
    const event = e.event as PointerEvent;

    // set position for the menu
    this.localStore.updateMenuPosition({ left: event.clientX, top: event.clientY });
    this.rowMenuTrigger.menuData = { location: e.data }; // pass location data
    this.rowMenuTrigger.openMenu();

    // handle what happens when another right-click happens when rowMenu is already open
    // (is registered as an event on the overlay pane & triggers the default browser context menu
    // -> we want to prevent that)
    setTimeout(() => { // wait until the overlay renders, then register listener

      const handleContextMenu = (e: MouseEvent) => {
        e.preventDefault(); // don't show browser context menu on right-click
        overlay.click(); // close the row menu on right-click by triggering a click event
      };

      const overlay = document.getElementsByClassName(
          'cdk-overlay-backdrop cdk-overlay-transparent-backdrop cdk-overlay-backdrop-showing',
      )[0] as HTMLElement;

      if (overlay) {
        this.localStore.updateListenerCB(
            // creates listener and returns a callback to remove this listener
            this.eventManager.addEventListener(overlay, 'contextmenu', handleContextMenu),
        );
      }
    }, 100);

    sendToGoogleAnalytics(
        'Single-location action',
        'show context menu',
        'Show parent location action menu on row right-click');
  }

  /**
   * Set focused row ID in local store
   * @param e
   * @param focusedRowId
   */
  onCellDblClicked(e: CellDoubleClickedEvent<Location>, focusedRowId: number) {
    if (focusedRowId !== e.data.id) {
      this.store.dispatch(RowActions.userFocusRow({ id: e.data.id }));
    }
  }

  /**
   * Sync hovered row ID in local store
   * @param e
   * @param hoveredRowId
   */
  onCellMouseOver(e: CellMouseOverEvent<Location>, hoveredRowId: number) {
    if (hoveredRowId !== e.data.id) {
      this.store.dispatch(RowActions.updateHoveredRowId({ hoveredRowId: e.data.id }));
    }
  }

  /**
   * Sync hovered row ID in local store
   */
  onCellMouseOut() {
    this.resetHoveredRow();
  }

  /**
   * Nullify hovered row ID when cursor leaves the grid container
   */
  onGridMouseLeave() {
    this.resetHoveredRow();
  }

  /**
   * Sync selected row IDs in local store
   * @param e
   */
  onSelectionChanged(e: SelectionChangedEvent<Location>) {
    this.store.dispatch(RowActions.updateSelectedRowsIds({
      selectedRowsIds:
          e.api.getSelectedRows()?.map(l => l.id),
    }));
  }

  /**
   * Removes the right-click ('contextmenu') listener on row menu's overlay element.
   */
  onRowMenuClosed() {
    this.localStore.removeOverlayRightClickListener$();
  }

  /**
   * @param event
   */
  onSortChanged(event: SortChangedEvent<Location>) {
    // sync store
    this.localStore.updateColumnState(event.api.getColumnState());
  }

  /**
   * Sync total amount of displayed locations and filter model in local store
   * Grid filter model changes > local store
   * @param event
   */
  onFilterChanged(event: FilterChangedEvent<Location>) {
    this.localStore.updateFilterModel(event.api.getFilterModel());
    this.localStore.updateTotal(event.api.getDisplayedRowCount());
    this.localStore.computeBubbles();
  }

  /**
   * Sync total amount of displayed locations in local store
   * @param event
   */
  onRowDataUpdated(event: RowDataUpdatedEvent<Location>) {
    this.localStore.updateTotal(event.api.getDisplayedRowCount());
    this.localStore.computeBubbles();
    // makes sure to rerun filtering/sorting after data changes, fixes BLS-6096
    this.locationListApi.gridApi?.onFilterChanged();
    this.locationListApi.gridApi?.onSortChanged();
  }

  /**
   * Sync pagination data in local store
   * Paginator > AgGrid > Local store
   * @param e
   * @param storedPageSize
   */
  syncPaginationData(e: PaginationChangedEvent, storedPageSize: number) {
    if (e.newPage) {
      this.localStore.updatePageIndex(e.api.paginationGetCurrentPage());
    }

    const gridPageSize = e.api.paginationGetPageSize();

    if (storedPageSize !== gridPageSize) {
      this.localStore.updatePageSize(gridPageSize);
    }
  }

  /**
   *
   * @param pageIndex 0-based
   * @param pageSize
   */
  setPaginatorData(pageIndex: number, pageSize: number) {
    this.locationListApi.goToPage(pageIndex);
    this.locationListApi.setPageSize(pageSize);
    this.rewriteMaxPageSizeValue();
  }

  /**
   * Toggle bottom panel and update state
   */
  onToggleBottomPanelClick() {
    this.store.dispatch(SkeletonBottomPanelActions.userToggleBottomPanel({}));
  }

  /**
   * Toggle right panel and update state
   */
  onToggleRightPanelClick() {
    this.store.dispatch(SkeletonRightPanelActions.userToggleRightPanel({}));
  }

  /**
   * Update Column State
   */
  onColumnVisible(e: ColumnVisibleEvent<Location>) {
    this.localStore.gridReady$
        .pipe(take(1))
        .subscribe((gridReady) => {
          // Filter out events until grid is ready
          // Leads to bugged UI otherwise since column options are being updated from local store
          // at component initiation and below code should run only if user changes col visibility
          if (!gridReady) return;

          // reset filter and sorting if a col was hidden by user
          e.columns.forEach(column => {
            if (!column.isVisible()) {
              if (column.getSort()) {
                // reset sort and sortIndex
                e.api.applyColumnState({
                  state: [{
                    colId: column.getColId(),
                    sort: null,
                    sortIndex: null
                  }]
                });
                // decrease sortIndex of other columns by 1
                e.api.getColumns().map(col => {
                  if (col.getSortIndex() && col.getId() !== column.getId()) {
                    e.api.applyColumnState({
                      state: [{
                        colId: col.getId(),
                        sortIndex: col.getSortIndex() - 1,
                      }]
                    });
                  }
                });
              }
              // reset filter
              e.api.setColumnFilterModel(column.getId(), null)
                  .then(() => e.api.onFilterChanged());
            }
          });

          // update local store
          this.localStore.updateColumnState(e.api.getColumnState());
        });
  }

  /**
   * Update Column State
   */
  onColumnPinned(e: ColumnPinnedEvent<Location>) {
    this.localStore.updateColumnState(e.api.getColumnState());
  }

  /**
   * Update Column State
   */
  onColumnMoved(e: ColumnMovedEvent<Location>) {
    this.localStore.updateColumnState(e.api.getColumnState());
  }

  /**
   * Update Column State
   */
  onColumnResized(e: ColumnResizedEvent<Location>) {
    this.localStore.updateColumnState(e.api.getColumnState());
  }

  onCellFocused(e: CellFocusedEvent<Location>) {
    const rowNode = e.api.getDisplayedRowAtIndex(e.rowIndex);
    const colDef = (e.column as Column)?.getColDef();
    const useFormatter = !!colDef?.valueFormatter;

    this.localStore.updateFocussedCellValue(e.api.getCellValue({
      rowNode,
      colKey: e.column,
      useFormatter
    }));
  }

  /**
   * Refocus cell. Called after opening a mat menu which normally gets focus after it;s rendered.
   */
  refocusCell() {
    this.locationListApi.refocusCell();
  }

  private resetHoveredRow() {
    this.store.dispatch(RowActions.updateHoveredRowId({ hoveredRowId: null }));
  }

  /**
   * Hack to rewrite selected pageSize in the select box if the value equals Number.MAX_VALUE
   * MatPaginator does not support 'All' option
   * https://github.com/angular/components/issues/8150
   * @private
   */
  private rewriteMaxPageSizeValue() {
    try {
      this.paginatorElement.nativeElement.querySelector('div.mat-mdc-select-value > span > span').innerHTML =
          this.paginator.pageSize === MAX_JAVA_INT ? 'All' : this.paginator.pageSize;
    } catch (e) {
      console.error(e);
    }
  }


  /**
   * Checks if a given column should be visible by default
   * @param fieldId
   * @private
   */
  private isVisibleByDefault(fieldId: LSCField | 'rowMenu') {
    return COLUMNS_VISIBLE_BY_DEFAULT.includes(fieldId);
  }

  private registerHotkeys() {
    this.hotkeysService.addHotkeyAndGetEventFeed('j', 'Previous page', this.componentDestroyed$ as any)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(() => {
          this.paginator.previousPage();
        });

    this.hotkeysService.addHotkeyAndGetEventFeed('k', 'Next page', this.componentDestroyed$ as any)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(() => {
          this.paginator.nextPage();
        });

    this.hotkeysService.addHotkeyAndGetEventFeed('control.enter', 'Apply filters', this.componentDestroyed$ as any)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(() => {
          this.locationListApi.gridApi?.onFilterChanged();
        });

    this.hotkeysService.addHotkeyAndGetEventFeed('shift.c', 'Copy cell', this.componentDestroyed$ as any)
        .pipe(concatLatestFrom(() => this.localStore.focussedCellValue$),
            filter(([_, focussedCellValue]) => Boolean(focussedCellValue)),
            takeUntil(this.componentDestroyed$))
        .subscribe(([_, focussedCellValue]) => {
          const copied = this.clipboard.copy(focussedCellValue);

          if (copied) {
            this.feedbackService.showSuccessSnackbar({data: {userFriendlyMessage: 'Cell copied'}});
          }
        });
  }


  /**
   * Generate column defs on demand
   * @private
   */
  private getColumnDefs(): ColDef[] {
    return [
      {
        headerName: getFullNameForFieldID(LSCField.storeCode),
        field: LSCField.storeCode,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          hasDataSourceIcon: isFieldWithDataSource(LSCField.storeCode),
          enableMenu: false,
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: AgChipFilterComponent,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgListFloatingFilterComponent,
        floatingFilterComponentParams: {
          label: 'Search',
        },
        pinned: 'left',
        lockPinned: true,
        width: 190,
        hide: !this.isVisibleByDefault(LSCField.storeCode),
      },
      {
        headerName: getFullNameForFieldID(LSCField.status),
        field: LSCField.status,
        // only needed here so that cell flashing works
        valueGetter: (params: ValueGetterParams<Location>) => params?.data?.businessStatus +
            ' ' + params?.data?.managementStatus,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          hasDataSourceIcon: isFieldWithDataSource(LSCField.status),
          enableMenu: false,
        } as DataColumnHeaderParams,
        cellRenderer: StatusCellComponent,
        filter: StatusFilterComponent,
        floatingFilter: true,
        floatingFilterComponent: AgListFloatingFilterComponent,
        floatingFilterComponentParams: { showActiveFilterAmountOnly: true } as ListFloatingFilterParams,
        width: 153,
        hide: !this.isVisibleByDefault(LSCField.status),
        autoHeight: true,
        editable: true,
        cellEditor: StatusEditorComponent,
        enableCellChangeFlash: true,
      },
      {
        headerName: getFullNameForFieldID(LSCField.comment),
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.comment),
        } as DataColumnHeaderParams,
        field: LSCField.comment,
        cellRenderer: SimpleTextCellComponent,
        cellEditor: SimpleTextEditorComponent,
        cellEditorParams: { validationPassed: null },
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        editable: true,
        width: 215,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.comment),
      },
      {
        headerName: getFullNameForFieldID(LSCField.businessName),
        field: LSCField.businessName,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.businessName),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        width: 250,
        hide: !this.isVisibleByDefault(LSCField.businessName),
      },
      {
        headerName: getFullNameForFieldID(LSCField.addressLine1),
        field: LSCField.addressLine1,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.addressLine1),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: RecentUpdateFloatingFilterComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        width: 210,
        hide: !this.isVisibleByDefault(LSCField.addressLine1),
      },
      {
        headerName: getFullNameForFieldID(LSCField.addressLine2),
        field: LSCField.addressLine2,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.addressLine2),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        width: 210,
        hide: !this.isVisibleByDefault(LSCField.addressLine2),
      },
      {
        headerName: getFullNameForFieldID(LSCField.addressLine3),
        field: LSCField.addressLine3,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.addressLine3),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        width: 210,
        hide: !this.isVisibleByDefault(LSCField.addressLine3),
      },
      {
        headerName: getFullNameForFieldID(LSCField.addressLine4),
        field: LSCField.addressLine4,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.addressLine4),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        width: 210,
        hide: !this.isVisibleByDefault(LSCField.addressLine4),
      },
      {
        headerName: getFullNameForFieldID(LSCField.addressLine5),
        field: LSCField.addressLine5,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.addressLine5),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        width: 210,
        hide: !this.isVisibleByDefault(LSCField.addressLine5),
      },
      {
        headerName: getFullNameForFieldID(LSCField.city),
        field: LSCField.city,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.city),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        width: 170,
        hide: !this.isVisibleByDefault(LSCField.city),
      },
      {
        headerName: getFullNameForFieldID(LSCField.postalCode),
        field: LSCField.postalCode,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.postalCode),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        width: 218,
        autoHeight: false,
        hide: !this.isVisibleByDefault(LSCField.postalCode),
      },
      {
        headerName: getFullNameForFieldID(LSCField.state),
        field: LSCField.state,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.state),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        width: 180,
        hide: !this.isVisibleByDefault(LSCField.state),
      },
      {
        headerName: getFullNameForFieldID(LSCField.country),
        field: LSCField.country,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          hasDataSourceIcon: isFieldWithDataSource(LSCField.country),
          enableMenu: false,
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: AgMultiSelectFilterComponent,
        filterParams: {
          filterOptions: this.store.select(selectCountryList).pipe(map((cl) => {
            return cl.map(country => ({ name: country, value: country }));
          })),
          gridContainerId: this.gridContainerId,
        },
        floatingFilter: true,
        floatingFilterComponent: AgListFloatingFilterComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        autoHeight: false,
        enableCellChangeFlash: true,
        width: 170,
        hide: !this.isVisibleByDefault(LSCField.country),
      },
      {
        headerName: getFullNameForFieldID(LSCField.website),
        field: LSCField.website,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.website),
        } as DataColumnHeaderParams,
        cellRenderer: LinkCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.website),
        width: 300,
      },
      {
        headerName: getFullNameForFieldID(LSCField.businessHours),
        field: LSCField.businessHours,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.businessHours),
        } as DataColumnHeaderParams,
        // cellRenderer does not need this concatenated value, but the filter does
        valueGetter: (params: ValueGetterParams<Location>) => {
          let str = '';
          if (params.data?.mondayHours) {
            str += 'Mon ' + params.data.mondayHours + '\n';
          }
          if (params.data?.tuesdayHours) {
            str += 'Tue ' + params.data.tuesdayHours + '\n';
          }
          if (params.data?.wednesdayHours) {
            str += 'Wed ' + params.data.wednesdayHours + '\n';
          }
          if (params.data?.thursdayHours) {
            str += 'Thu ' + params.data.thursdayHours + '\n';
          }
          if (params.data?.fridayHours) {
            str += 'Fri ' + params.data.fridayHours + '\n';
          }
          if (params.data?.saturdayHours) {
            str += 'Sat ' + params.data.saturdayHours + '\n';
          }
          if (params.data?.sundayHours) {
            str += 'Sun ' + params.data.sundayHours + '\n';
          }
          return str;
        },
        cellRenderer: BusinessHoursCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          // clear the filter text + filter value of ',' '\s' '\n' and day acronyms prepended in the cell renderer
          textFormatter: (from: string) => from.replace(/[,\s\nMon|Tue|Wed|Thu|Fri|Sat|Sun]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 246,
        hide: !this.isVisibleByDefault(LSCField.businessHours),
        editable: true,
        cellEditor: BusinessHoursEditorComponent,
        enableCellChangeFlash: true,
      },
      {
        headerName: getFullNameForFieldID(LSCField.differences),
        field: LSCField.differences,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          hasDataSourceIcon: isFieldWithDataSource(LSCField.differences),
          enableMenu: false,
        } as DataColumnHeaderParams,
        valueGetter: (params: ValueGetterParams<Location>) => getDifferenceFieldsAsString(params.data?.differences),
        cellRenderer: (params: ICellRendererParams) =>
            `<div class="locationGridCell">${params.value}</div>`,
        filter: AgMultiSelectFilterComponent,
        filterParams: {
          filterOptions: getFilterableGmbFields()
              .map(fieldId => ({
                name: getFullNameForFieldID(fieldId),
                value: getFullNameForFieldID(fieldId),
              })),
          gridContainerId: this.gridContainerId,
        },
        floatingFilter: true,
        floatingFilterComponent: AgListFloatingFilterComponent,
        width: 212,
        cellStyle: { whiteSpace: 'pre-line' },
        hide: !this.isVisibleByDefault(LSCField.differences),
      },
      {
        headerName: getFullNameForFieldID(LSCField.specialHours),
        field: LSCField.specialHours,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.specialHours),
        } as DataColumnHeaderParams,
        cellRenderer: MultiLineTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 246,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.specialHours),
      },
      {
        headerName: getFullNameForFieldID(LSCField.gxSpecialHours),
        field: LSCField.gxSpecialHours,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.gxSpecialHours),
        } as DataColumnHeaderParams,
        cellRenderer: MultiLineTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 246,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.gxSpecialHours),
      },
      {
        headerName: getFullNameForFieldID(LSCField.openingDate),
        field: LSCField.openingDate,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.openingDate),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: 'agTextColumnFilter',
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 226,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.openingDate),
      },
      {
        headerName: getFullNameForFieldID(LSCField.district),
        field: LSCField.district,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.district),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 200,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.district),
      },
      {
        headerName: getFullNameForFieldID(LSCField.coordinateImport),
        field: LSCField.coordinateImport,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.coordinateImport),
        } as DataColumnHeaderParams,
        valueGetter: (params: ValueGetterParams<Location>) => {
          let str = '';
          if (params.data?.coordinateImport?.latitude) str += params.data?.coordinateImport?.latitude + ',';
          if (params.data?.coordinateImport?.longitude) str += params.data?.coordinateImport?.longitude;
          return str;
        },
        valueFormatter: (params: ValueFormatterParams<Location>) => params.value?.split(',').join(',\n'),
        cellRenderer: CoordinateCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: RecentUpdateFloatingFilterComponent,
        width: 276,
        hide: !this.isVisibleByDefault(LSCField.coordinateImport),
        editable: true,
        cellEditor: CoordinatesEditorComponent,
        enableCellChangeFlash: true,
      },
      {
        headerName: getFullNameForFieldID(LSCField.coordinate),
        field: LSCField.coordinate,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.coordinate),
        } as DataColumnHeaderParams,
        valueGetter: (params: ValueGetterParams<Location>) => {
          let str = '';
          if (params.data?.coordinate?.latitude) str += params.data?.coordinate?.latitude + ',';
          if (params.data?.coordinate?.longitude) str += params.data?.coordinate?.longitude;
          return str;
        },
        valueFormatter: (params: ValueFormatterParams<Location>) => params.value?.split(',').join(',\n'),
        cellRenderer: CoordinateCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 266,
        hide: !this.isVisibleByDefault(LSCField.coordinate),
      },
      {
        headerName: getFullNameForFieldID(LSCField.coverPhoto),
        field: LSCField.coverPhoto,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.coverPhoto),
        } as DataColumnHeaderParams,
        cellRenderer: PhotoCellComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 260,
        hide: !this.isVisibleByDefault(LSCField.coverPhoto),
      },
      {
        headerName: getFullNameForFieldID(LSCField.logoPhoto),
        field: LSCField.logoPhoto,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.logoPhoto),
        } as DataColumnHeaderParams,
        cellRenderer: PhotoCellComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 260,
        hide: !this.isVisibleByDefault(LSCField.logoPhoto),
      },
      {
        headerName: getFullNameForFieldID(LSCField.otherPhotos),
        field: LSCField.otherPhotos,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.otherPhotos),
          staticColumnNote: 'Other Photos can be blacklisted and updated on GBP but we do not support differences for this field right now.',
        } as DataColumnHeaderParams,
        cellRenderer: PhotoCellComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 280,
        hide: !this.isVisibleByDefault(LSCField.otherPhotos),
      },
      {
        headerName: getFullNameForFieldID(LSCField.primaryPhone),
        field: LSCField.primaryPhone,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.primaryPhone),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 240,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.primaryPhone),
      },
      {
        headerName: getFullNameForFieldID(LSCField.additionalPhone),
        field: LSCField.additionalPhone,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.additionalPhone),
        } as DataColumnHeaderParams,
        cellRenderer: MultiLineTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 255,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.additionalPhone),
      },
      {
        headerName: getFullNameForFieldID(LSCField.adwordsLocation),
        field: LSCField.adwordsLocation,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.adwordsLocation),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 260,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.adwordsLocation),
      },
      {
        headerName: getFullNameForFieldID(LSCField.primaryCategory),
        field: LSCField.primaryCategory,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.primaryCategory),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 248,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.primaryCategory),
      },
      {
        headerName: getFullNameForFieldID(LSCField.additionalCategory),
        field: LSCField.additionalCategory,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.additionalCategory),
        } as DataColumnHeaderParams,
        cellRenderer: MultiLineTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 276,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.additionalCategory),
      },
      {
        headerName: getFullNameForFieldID(LSCField.fromTheBusiness),
        field: LSCField.fromTheBusiness,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.fromTheBusiness),
        } as DataColumnHeaderParams,
        cellRenderer: SimpleTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 260,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.fromTheBusiness),
      },
      {
        headerName: getFullNameForFieldID(LSCField.labels),
        field: LSCField.labels,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.labels),
        } as DataColumnHeaderParams,
        cellRenderer: MultiLineTextCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 195,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.labels),
      },
      {
        headerName: getFullNameForFieldID(LSCField.customServices),
        field: LSCField.customServices,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.customServices),
        } as DataColumnHeaderParams,
        valueGetter: (params: ValueGetterParams<Location>) => {
          let text = '';
          params.data?.customServices?.forEach((customService, i) => {
            text +=
                getCustomServiceAsString(customService) +
                (i < params.data?.customServices?.length - 1 ? ';' : '');
          });
          return text;
        },
        cellRenderer: MultiLineTextCellComponent,
        cellRendererParams: {
          splitSeparator: ';',
          joinSeparator: ',\n\n',
        } as MultiLineTextCellComponentParams,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
          // clear out the additional chars added by valueGetter above + comma, whitespace and newline chars
          textFormatter: (from: string) => from.replace(/[,;:()\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 325,
        hide: !this.isVisibleByDefault(LSCField.customServices),
        editable: true,
        enableCellChangeFlash: true,
        cellEditor: CustomServicesEditorComponent,

      },
      {
        headerName: getFullNameForFieldID(LSCField.menuURL),
        field: LSCField.menuURL,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.menuURL),
          staticColumnNote: 'Menu URL can be blacklisted and updated on GBP but we do not support differences for this field right now',
        } as DataColumnHeaderParams,
        cellRenderer: LinkCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        width: 230,
        hide: !this.isVisibleByDefault(LSCField.menuURL),
      },
      {
        headerName: getFullNameForFieldID(LSCField.appointmentURL),
        field: LSCField.appointmentURL,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.appointmentURL),
          staticColumnNote: 'URLs used for booking appointments, separated by a comma.\n\nGBP only allows this for locations with verified ownership!',
        } as DataColumnHeaderParams,
        cellRenderer: LinkCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 280,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.appointmentURL),
      },
      {
        headerName: getFullNameForFieldID(LSCField.orderAheadURL),
        field: LSCField.orderAheadURL,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.orderAheadURL),
          staticColumnNote: 'URLs used for ordering food for delivery and/or takeout, separated by a comma.\n\nGBP only allows this for locations with verified ownership!',
        } as DataColumnHeaderParams,
        cellRenderer: LinkCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 276,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.orderAheadURL),
      },
      {
        headerName: getFullNameForFieldID(LSCField.reservationsURL),
        field: LSCField.reservationsURL,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.reservationsURL),
          staticColumnNote: 'URLs used for making dining reservations, separated by a comma.\n\nGBP only allows this for locations with verified ownership!',
        } as DataColumnHeaderParams,
        cellRenderer: LinkCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 280,
        editable: true,
        cellEditor: SimpleTextEditorComponent,
        enableCellChangeFlash: true,
        hide: !this.isVisibleByDefault(LSCField.reservationsURL),
      },
      {
        headerName: getFullNameForFieldID(LSCField.socialMediaUrls),
        field: LSCField.socialMediaUrls,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.socialMediaUrls),
        } as DataColumnHeaderParams,
        valueGetter: (params: ValueGetterParams<Location>) => {
          if (params.data?.socialMediaUrls) {
            return Object.values(params.data.socialMediaUrls).map((link: string) => link.trim()).join();
          }
          return undefined;
        },
        cellRenderer: LinkCellComponent,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          textFormatter: (from: string) => from.replace(/[,\s\n]+/g, ''),
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 350,
        hide: !this.isVisibleByDefault(LSCField.socialMediaUrls),
        editable: true,
        cellEditor: SocialMediaLinksEditorComponent,
        enableCellChangeFlash: true,
      },
      {
        headerName: getFullNameForFieldID(LSCField.gmbAccountIds),
        field: LSCField.gmbAccountIds,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          hasDataSourceIcon: isFieldWithDataSource(LSCField.gmbAccountIds),
          enableMenu: false,
        } as DataColumnHeaderParams,
        filter: AgMultiSelectFilterComponent,
        valueGetter: (params: ValueGetterParams<Location>) => {
          const gmbAccountNames: string[] = [];

          params.data.gmbAccountIds.forEach((gmbAccountId) => {
            const gmbAccountName = this.gbpLocationGroups.value.find(gbpGroup =>
                gbpGroup.gmbAccountId === gmbAccountId)?.accountName;

            if (gmbAccountName) {
              gmbAccountNames.push(gmbAccountName);
            }
          });

          return gmbAccountNames;
        },
        cellRenderer: (params: ICellRendererParams) =>
            `<div class="locationGridCell">${params.valueFormatted}</div>`,
        valueFormatter: (params: ValueFormatterParams<Location>) => params?.value?.join(',\n'),
        filterParams: {
          filterOptions: this.store.select(selectGbpLocationGroups)
              .pipe(map((groups) => groups
                  .map(grp => ({
                        name: grp.accountName,
                        value: grp.accountName,
                      }),
                  ))),
          gridContainerId: this.gridContainerId,
          searchFieldHidden: false,
        },
        floatingFilter: true,
        floatingFilterComponent: AgListFloatingFilterComponent,
        hide: !this.isVisibleByDefault(LSCField.gmbAccountIds),
        width: 215,
      },
      {
        headerName: getFullNameForFieldID(LSCField.gmbLocationId),
        field: LSCField.gmbLocationId,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          hasDataSourceIcon: isFieldWithDataSource(LSCField.gmbLocationId),
          enableMenu: false,
        } as DataColumnHeaderParams,
        filter: AgChipFilterComponent,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgListFloatingFilterComponent,
        floatingFilterComponentParams: {
          label: 'Search',
        },
        width: 150,
        hide: !this.isVisibleByDefault(LSCField.gmbLocationId),
      },
      {
        headerName: getFullNameForFieldID(LSCField.lastImportErrorMessage),
        field: LSCField.lastImportErrorMessage,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.lastImportErrorMessage),
        } as DataColumnHeaderParams,
        valueFormatter: (params: ValueFormatterParams<Location>) => params.value ? params.value : '',
        cellRenderer: (params: ICellRendererParams) =>
            `<div class="locationGridCell">${params.valueFormatted}</div>`,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        hide: !this.isVisibleByDefault(LSCField.lastImportErrorMessage),
        width: 300,
      },
      {
        headerName: getFullNameForFieldID(LSCField.lastUpdateErrorMessage),
        field: LSCField.lastUpdateErrorMessage,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.lastUpdateErrorMessage),
        } as DataColumnHeaderParams,
        valueFormatter: (params: ValueFormatterParams<Location>) => params.value ? params.value : '',
        cellRenderer: (params: ICellRendererParams) =>
            `<div class="locationGridCell">${params.valueFormatted}</div>`,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        hide: !this.isVisibleByDefault(LSCField.lastUpdateErrorMessage),
        width: 300,
      },
      {
        headerName: getFullNameForFieldID(LSCField.blackListedFields),
        field: LSCField.blackListedFields,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          hasDataSourceIcon: isFieldWithDataSource(LSCField.blackListedFields),
          enableMenu: false,
          staticColumnNote: 'Prevents the blacklisted LSC data from reaching GBP.',
        } as DataColumnHeaderParams,
        valueGetter: (params: ValueGetterParams<Location>) => getBlacklistedFieldsAsString(params.data?.blackListedFields),
        cellRenderer: (params: ICellRendererParams) =>
            `<div class="locationGridCell">${params.value}</div>`,
        filter: AgMultiSelectFilterComponent,
        filterParams: {
          buttons: ['reset'],
          filterOptions: getChangeableGmbFields()
              .map(gmbField => ({
                name: getFullNameForFieldID(gmbField),
                value: getFullNameForFieldID(gmbField),
              })),
          gridContainerId: this.gridContainerId,
        },
        floatingFilter: true,
        floatingFilterComponent: AgListFloatingFilterComponent,
        width: 270,
        hide: !this.isVisibleByDefault(LSCField.blackListedFields),
      },
      {
        headerName: getFullNameForFieldID(LSCField.acceptedDifferences),
        field: LSCField.acceptedDifferences,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          hasDataSourceIcon: isFieldWithDataSource(LSCField.acceptedDifferences),
          enableMenu: false,
          staticColumnNote: 'For all BLS locations that have a linked GBP location, all fields are compared at least once per day (this may additionallly be triggered manually by calling “Get updates from GBP”). If something in GBP has changed since yesterday, the BLS user will be able to tell due to a new open difference for the fields that were changed. The LEs have to decide how to handle these differences:\n1) either it is functionally the same (e.g. Hauptstr. vs Hauptstraße),\n2) the BLS data is incorrect, the data on GBP is correct, or\n3) the BLS data is correct, the data on GBP is incorrect.\nIn cases 1) and 2), the differences are “accepted”. Nothing is changed on GBP. The difference is essentially marked as “read”. In case 3) the LE may decide to override the GBP data by using the “Update in GBP” feature. Alternatively, they talk to the customer directly to sort out those differences.',
        } as DataColumnHeaderParams,
        valueGetter: (params: ValueGetterParams<Location>) => getDifferenceFieldsAsString(params.data?.differences, true),
        cellRenderer: (params: ICellRendererParams) =>
            `<div class="locationGridCell">${params.value}</div>`,
        filter: AgMultiSelectFilterComponent,
        filterParams: {
          buttons: ['reset'],
          filterOptions: getChangeableGmbFields()
              .map(fieldId => ({
                name: getFullNameForFieldID(fieldId),
                value: getFullNameForFieldID(fieldId),
              })),
          gridContainerId: this.gridContainerId,
        },
        floatingFilter: true,
        floatingFilterComponent: AgListFloatingFilterComponent,
        width: 270,
        hide: !this.isVisibleByDefault(LSCField.acceptedDifferences),
      },
      {
        headerName: getFullNameForFieldID(LSCField.editTimestamp),
        field: LSCField.editTimestamp,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          hasDataSourceIcon: isFieldWithDataSource(LSCField.editTimestamp),
          enableMenu: true,
        } as DataColumnHeaderParams,
        valueGetter: (params: ValueGetterParams<Location>) => {
          return params.data?.editTimestamp ? new Date(params.data.editTimestamp) : null;
        },
        valueFormatter: (params: ValueFormatterParams<Location>) => {
          return params.value ? formatLocalizedDate(params.value, 'dd.MM.yyyy, HH:mm') : '';
        },
        filter: AgDateTimeFilterComponent,
        floatingFilter: true,
        floatingFilterComponent: AgDateTimeFloatingFilterComponent,
        width: 200,
        hide: !this.isVisibleByDefault(LSCField.editTimestamp),
      },
      {
        headerName: getFullNameForFieldID(LSCField.dataGoldmine),
        field: LSCField.dataGoldmine,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          enableMenu: true,
          hasDataSourceIcon: isFieldWithDataSource(LSCField.dataGoldmine),
        } as DataColumnHeaderParams,
        valueGetter: (params: ValueGetterParams<Location>) =>
            prettifyJSON(params.data?.dataGoldmine),
        cellRenderer: (params: ICellRendererParams) =>
            `<div class="locationGridCell text-[12px]">${params.value}</div>`,
        filter: true,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgTextFloatingFilterComponent,
        width: 350,
        hide: !this.isVisibleByDefault(LSCField.dataGoldmine),
      },
      {
        headerName: getFullNameForFieldID(LSCField.claimAndCleanCompletionTimestamp),
        field: LSCField.claimAndCleanCompletionTimestamp,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          hasDataSourceIcon: isFieldWithDataSource(LSCField.claimAndCleanCompletionTimestamp),
          enableMenu: true,
        } as DataColumnHeaderParams,
        cellRenderer: CcCompletionCellComponent,
        valueGetter: (params: ValueGetterParams<Location>) => {
          return params.data?.claimAndCleanCompletionTimestamp ? new Date(params.data.claimAndCleanCompletionTimestamp) : null;
        },
        valueFormatter: (params: ValueFormatterParams<Location>) => {
          return params.value ? formatLocalizedDate(params.value, 'dd.MM.yyyy, HH:mm') : '';
        },
        filter: AgDateTimeFilterComponent,
        floatingFilter: true,
        floatingFilterComponent: AgDateTimeFloatingFilterComponent,
        cellStyle: {
          paddingTop: 0,
          paddingBottom: 0,
        },
        width: 230,
        hide: !this.isVisibleByDefault(LSCField.claimAndCleanCompletionTimestamp),
      },
      {
        headerName: getFullNameForFieldID(LSCField.id),
        field: LSCField.id,
        headerComponent: DataColumnHeaderComponent,
        headerComponentParams: {
          hasDataSourceIcon: isFieldWithDataSource(LSCField.id),
          enableMenu: false,
        } as DataColumnHeaderParams,
        filterValueGetter: (params: ValueGetterParams<Location>) => params.data?.id?.toString(10),
        filter: AgChipFilterComponent,
        filterParams: {
          buttons: ['reset'],
          trimInput: true,
        } as ITextFilterParams,
        floatingFilter: true,
        floatingFilterComponent: AgListFloatingFilterComponent,
        floatingFilterComponentParams: {
          label: 'Search',
        },
        width: 200,
        hide: !this.isVisibleByDefault(LSCField.id),
      },
      {
        headerName: '',
        valueGetter: (params: ValueGetterParams<Location>) => params,
        field: 'rowMenu',
        headerComponent: RowMenuHeaderComponent,
        cellRenderer: RowMenuCellComponent,
        pinned: 'right',
        lockPinned: true,
        minWidth: 196,
        maxWidth: 196,
        cellStyle: {
          paddingTop: '3px',
          paddingBottom: '3px',
          paddingLeft: '5px',
          paddingRight: '5px',
        },
        hide: !this.isVisibleByDefault('rowMenu'),
        resizable: false,
      },
    ];
  }


}


